import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { Breadcrumbs } from './Breadcrumbs';
import { SearchPalette } from './SearchPalette';
import { useSidebarState } from '@/hooks/use-sidebar-state';
import { cn } from '@/lib/utils';
import { BillingSetupBanner } from '@/features/billing/BillingSetupBanner';
import { GettingStartedHelp } from '@/components/layout/GettingStartedHelp';
import { useAuth } from '@/stores/use-auth';
import { getSidebarNavItems } from '@/lib/navigation-config';
import { useCurrentOrganization } from '@/hooks/use-organization';
import { useGovernanceStandardsWorkflowsGate } from '@/features/work-items/hooks/useGovernanceStandardsWorkflowsGate';
import { useInboxDocumentTitle } from '@/features/inbox/hooks/useInboxDocumentTitle';
import { BreadcrumbTrailingSlotContext } from './breadcrumbTrailingSlot';

interface DashboardLayoutProps {
  children: ReactNode;
}

/** What a page may change about the shell around it; the default keeps the shell as it is. */
export interface DashboardPageChrome {
  /** One 24 px gutter and no max width (the shell's container otherwise caps and pads the page). */
  wide?: boolean;
  /** No floating Quickstart button; the sheet still opens on `QUICKSTART_OPEN_EVENT`. */
  hideQuickstartLauncher?: boolean;
  /**
   * The Quickstart sheet does not open by itself over this page (once per session elsewhere); the
   * session's auto-open is left for the next page.
   */
  suppressQuickstartAutoOpen?: boolean;
  /**
   * The breadcrumb row gets an empty element at its right end that the page fills through
   * `useBreadcrumbTrailingSlot` (a portal). Without it the row is exactly the default one.
   */
  breadcrumbTrailing?: boolean;
}

const DashboardPageChromeContext = createContext<
  ((chrome: DashboardPageChrome | null) => void) | null
>(null);

/**
 * Lets the current page change the shell's chrome while it is mounted (`null` = default). Outside
 * the dashboard layout it does nothing.
 */
export function useDashboardPageChrome(chrome: DashboardPageChrome | null): void {
  const setChrome = useContext(DashboardPageChromeContext);
  // The flags, not the object: a page may pass a fresh object every render.
  const active = chrome !== null;
  const wide = chrome?.wide ?? false;
  const hideQuickstartLauncher = chrome?.hideQuickstartLauncher ?? false;
  const suppressQuickstartAutoOpen = chrome?.suppressQuickstartAutoOpen ?? false;
  const breadcrumbTrailing = chrome?.breadcrumbTrailing ?? false;
  useLayoutEffect(() => {
    if (!setChrome || !active) return undefined;
    setChrome({
      wide,
      hideQuickstartLauncher,
      ...(suppressQuickstartAutoOpen ? { suppressQuickstartAutoOpen } : {}),
      ...(breadcrumbTrailing ? { breadcrumbTrailing } : {}),
    });
    return () => setChrome(null);
  }, [
    setChrome,
    active,
    wide,
    hideQuickstartLauncher,
    suppressQuickstartAutoOpen,
    breadcrumbTrailing,
  ]);
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { t } = useTranslation('layout');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { isCollapsed, toggleSidebar } = useSidebarState();
  const [pageChrome, setPageChrome] = useState<DashboardPageChrome | null>(null);
  // Set by the trailing element's ref; back to null when it unmounts (another page, flag off).
  const [breadcrumbSlot, setBreadcrumbSlot] = useState<HTMLElement | null>(null);

  const eligibleForNextShell = useAuth(
    (s) => s.user?.nextNavigation?.eligibleForNextShell ?? false
  );
  const { organization } = useCurrentOrganization();
  const standardsGate = useGovernanceStandardsWorkflowsGate(organization?.organizationId);
  useInboxDocumentTitle(organization?.organizationId);
  const sidebarNavItems = useMemo(
    () =>
      getSidebarNavItems(eligibleForNextShell, {
        ...(standardsGate.data === false ? { governanceStandardsWorkflowsEnabled: false } : {}),
      }),
    [eligibleForNextShell, standardsGate.data]
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <DashboardPageChromeContext.Provider value={setPageChrome}>
      <div
        className={cn(
          'flex h-screen overflow-hidden bg-background',
          eligibleForNextShell && 'next-shell-layout'
        )}
      >
        <Sidebar
          isMobileOpen={isMobileOpen}
          onMobileClose={() => setIsMobileOpen(false)}
          isCollapsed={isCollapsed}
          onToggleCollapse={toggleSidebar}
          navItems={sidebarNavItems}
        />
        <div className="relative flex min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <TopBar
            onMenuClick={() => setIsMobileOpen(true)}
            onSearchClick={() => setIsSearchOpen(true)}
          />
          <main
            aria-label={t('main.ariaLabel')}
            className={cn('min-w-0 flex-1', isCollapsed ? 'lg:ml-0' : 'lg:ml-0')}
          >
            <div
              className={
                pageChrome?.wide
                  ? 'min-w-0 p-4 md:p-6'
                  : 'mx-auto max-w-screen-2xl min-w-0 p-4 md:p-6 2xl:p-10'
              }
            >
              {pageChrome?.breadcrumbTrailing ? (
                <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <Breadcrumbs className="min-w-0" />
                  <div
                    ref={setBreadcrumbSlot}
                    className="ml-auto min-w-0"
                    data-testid="breadcrumb-trailing-slot"
                  />
                </div>
              ) : (
                <Breadcrumbs className="mb-4" />
              )}
              <BillingSetupBanner />
              <BreadcrumbTrailingSlotContext.Provider value={breadcrumbSlot}>
                {children}
              </BreadcrumbTrailingSlotContext.Provider>
            </div>
          </main>
        </div>

        <SearchPalette isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
        <GettingStartedHelp
          hideLauncher={pageChrome?.hideQuickstartLauncher ?? false}
          suppressAutoOpen={pageChrome?.suppressQuickstartAutoOpen ?? false}
        />
      </div>
    </DashboardPageChromeContext.Provider>
  );
};
